import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ZbSummary, { fmtDate } from '../components/ZbSummary';
import RowBreakdown from '../components/RowBreakdown';
import { describeAcceptResult } from '../utils/deliveryNotices';
import DeliveryLeadsPanel from '../components/DeliveryLeadsPanel';
import CollapsibleSection from '../components/CollapsibleSection';
import ProgramCarousel, { PROGRAM_STATUS_COLORS } from '../components/ProgramCarousel';
import Section from '../components/Section';
import TaskList from '../components/TaskList';
import { goalsToPerGoalRows } from '../utils/statusBreakdown';
import { buildStatusGroupMap, splitByStatusGroup } from '../utils/statusGroups';
import { useTaskActions } from '../hooks/useTaskActions';
import { LEAD_STATUS_COLORS } from '../utils/leadStatus';

const API = process.env.REACT_APP_API_URL;

// Section header count-badge colors (2026-09-03, explicit ask) — grey by
// default, blue only when the badge represents something actually needing
// action. "Assigned Programs & Lead Status" and "Past Deliveries" are purely
// informational (how many programs / how many past deliveries, not how many
// need attention) so both stay grey unconditionally; "Your Tasks" and
// "Pending Deliveries" flip to blue only when their own count is nonzero.
// Section.js's `countColor` prop defaults to the original blue, so "Next
// Actions" on this same page and every Section elsewhere (SupplierPortal.js)
// is unaffected.
const BADGE_NEUTRAL = '#95a5a6';
const BADGE_ACTIVE = '#3498db';

const DELIVERY_STATUS_STYLES = {
  'Pending':         { bg: '#fef9e7', color: '#b7770d', border: '#f39c12' },
  'Delivered to PM': { bg: '#eafaf1', color: '#1e8449', border: '#27ae60' },
};

const URGENCY = {
  high:   { border: '#e67e22', bg: '#fffaf5', labelColor: '#c0392b', dot: '#e67e22' },
  normal: { border: '#3498db', bg: '#f0f7ff', labelColor: '#1a5276', dot: '#3498db' },
};

function statusBadge(status) {
  const s = DELIVERY_STATUS_STYLES[status] || { bg: '#f5f5f5', color: '#555', border: '#ccc' };
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap',
    }}>
      {status}
    </span>
  );
}

// ── Lead status summary bar (above Your Tasks) ──────────────────────────────────
// A one-line, cross-program pipeline snapshot — added 2026-09-03, explicit ask
// ("a 1 row compact view of status across all programs... mirror the compact
// programs view styling"). Reuses Programs.js's Compact view's own colored-pill
// treatment (LEAD_STATUS_COLORS, same rounded/white-on-color chip) rather than
// re-deriving new colors, but is deliberately its own small local component
// rather than a shared one — Programs.js's CompactProgramTable is a full
// DataTable-driven per-program table (sortable columns, row click-through);
// this is a single always-summed strip with a fixed 5-status set, a different
// enough shape that force-fitting it through the same component would mean
// bending CompactProgramTable's own props around a case it was never designed
// for. Data comes from GET /programs/lead-summary — already scoped exactly
// like GET /programs/ (pm_program_scope_filter()), so "All Assigned Programs"
// here can never include a program Edit/Drilldown/Compact would hide, and no
// second scoping rule needed inventing.
const HOME_STATUS_SUMMARY_STATUSES = [
  'Pending Review',
  'Under Review',
  'Approved Pending Call Review',
  'DemandArc Accepted',
  'Client Accepted',
];

// Display-only shortening for the one chip whose real status string is far
// wider than the other 4 (2026-09-03, real report — even after tightening
// spacing/fonts, "Approved Pending Call Review" alone was still the reason
// this row needed to scroll for perfectly ordinary numbers). "Call Reviews"
// is not invented wording — it's the exact activity_type this same page's
// Your Tasks feed already uses for this exact concept (TaskList.js/
// WeekAtAGlance.js), so it stays recognizable rather than a new abbreviation
// nobody's seen. The real status string is untouched everywhere else —
// summing (`totals[status]`), the `?status=` deep-link, and the tooltip
// text all still use the full value; only the rendered pill text is short.
const HOME_STATUS_SUMMARY_LABELS = {
  'Approved Pending Call Review': 'Call Reviews',
};

function LeadStatusSummaryBar({ rows, programIds }) {
  // Scoped to `programIds` — this page's own already-scoped `data.programs`
  // list — rather than trusting GET /programs/lead-summary's own role-based
  // scoping to agree with "assigned to me" (2026-09-03, real mismatch found
  // in testing): GET /home/pm applies pm_program_scope_filter() to every
  // role INCLUDING admin (an admin's My Home only ever shows programs where
  // they're literally the PM/Supporting PM, same as anyone else), while GET
  // /programs/lead-summary deliberately gives admin/financial every program
  // company-wide, unscoped — see that endpoint's own docstring. Without this
  // filter, an admin viewing My Home saw "All Assigned Programs (2)" next to
  // totals actually summed across every program in the system. Filtering to
  // the same id set this page already calls "assigned" makes the two always
  // agree, for every role, without touching the backend endpoint at all.
  // The caller passes `activePrograms.map(p => p.id)`, not the full raw
  // `programs` list (2026-09-03, real ask — "make sure the counts...does
  // not include closed programs") — same active/closed split
  // `splitByStatusGroup()` already computes for the Assigned Programs
  // carousel below, just reused here too so a closed program's leads never
  // inflate this bar's totals or its own "(N)" count.
  const idSet = new Set(programIds || []);
  const scopedRows = (rows || []).filter(p => idSet.has(p.program_id));
  const totals = HOME_STATUS_SUMMARY_STATUSES.reduce((acc, status) => {
    acc[status] = scopedRows.reduce((n, p) => n + (p.by_status?.[status] || 0), 0);
    return acc;
  }, {});
  // Each chip links out (2026-09-03, explicit ask). 4 of the 5 statuses have
  // a real Lead QA Review tab — LeadQA.js's own `qaFilter` values, deep-
  // linked via a new `?status=` param (mirrors the existing `?program_id=`
  // idiom already used for My Home's task CTAs). Deliberately NOT scoped to
  // any one program_id here — this bar sums across every assigned program at
  // once, so there is no single "the" program to hand Lead QA Review; it
  // opens with the status tab pre-armed and the program picker still to be
  // made by hand, same as opening the page fresh. Opens in a new tab, same
  // convention as every other My Home CTA (never takes the PM off their own
  // task list).
  // "Pending Review" has no tab at all — GET /leads/ never returns a
  // Pending Review lead (see LeadQA.js's own "No 'Pending Review' tab on
  // purpose" note), so sending it to Lead QA Review would land on a status
  // nothing there can ever show. It scrolls to Pending Deliveries instead —
  // the section on this same page where those leads actually live before
  // being accepted — same in-page-scroll idiom the Scrub task CTA already
  // uses for #pending-deliveries.
  const handleStatusChipClick = (status) => {
    if (status === 'Pending Review') {
      document.getElementById('pending-deliveries')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.open(`/lead-qa?status=${encodeURIComponent(status)}`, '_blank');
  };
  return (
    // Deliberately one row, never wrapping (2026-09-03, real report — a
    // large Client Accepted count pushed the last chip onto its own line).
    // flexWrap:'nowrap' + its own overflowX:'auto' guarantees that even on
    // a narrow viewport or with a much larger real count than this account
    // happens to show, the row scrolls horizontally in its own container
    // instead of breaking onto a second line — same "wide content scrolls,
    // the page never does" convention used everywhere else in this app.
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '7px',
      background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px',
      padding: '9px 14px', marginBottom: '14px', overflowX: 'auto',
    }}>
      <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#7f8c8d', whiteSpace: 'nowrap', flexShrink: 0 }}>
        All Assigned Programs ({(programIds || []).length})
      </span>
      {HOME_STATUS_SUMMARY_STATUSES.map(status => (
        <button
          key={status}
          onClick={() => handleStatusChipClick(status)}
          title={
            status === 'Pending Review'
              ? 'Jump to Pending Deliveries — Lead QA Review never shows Pending Review leads'
              : `Open Lead QA Review, filtered to ${status}`
          }
          style={{
            display: 'flex', alignItems: 'center', gap: '4px', background: 'none',
            border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          <span style={{
            background: LEAD_STATUS_COLORS[status]?.color, color: 'white', borderRadius: '3px',
            padding: '1px 6px', fontSize: '10.5px', whiteSpace: 'nowrap',
          }}>
            {HOME_STATUS_SUMMARY_LABELS[status] || status}
          </span>
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#2c3e50', whiteSpace: 'nowrap' }}>
            {(totals[status] || 0).toLocaleString()}
          </span>
        </button>
      ))}
    </div>
  );
}

// ── Next Actions ──────────────────────────────────────────────────────────────

function ActionButton({ action, onClick }) {
  const u = URGENCY[action.urgency] || URGENCY.normal;
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        gap: '3px', padding: '10px 14px', cursor: 'pointer', textAlign: 'left',
        background: hovered ? u.border : u.bg,
        border: `1.5px solid ${u.border}`,
        borderRadius: '8px', transition: 'background 0.15s',
        minWidth: '180px', maxWidth: '240px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '15px', lineHeight: 1 }}>{action.icon}</span>
        <span style={{
          fontSize: '12px', fontWeight: '700',
          color: hovered ? 'white' : u.labelColor,
          lineHeight: '1.3',
        }}>
          {action.label}
        </span>
      </div>
      <span style={{
        fontSize: '11px', color: hovered ? 'rgba(255,255,255,0.85)' : '#7f8c8d',
        lineHeight: '1.3', paddingLeft: '21px',
      }}>
        {action.detail}
      </span>
    </button>
  );
}

function NextActionsSection({ programs, onActionClick }) {
  const programsWithActions = programs.filter(p => p.next_actions?.length > 0);
  if (programsWithActions.length === 0) return null;

  return (
    <Section
      title="Next Actions"
      count={programsWithActions.reduce((n, p) => n + p.next_actions.length, 0)}
      defaultOpen
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {programsWithActions.map(p => (
          <div key={p.id} style={{
            background: 'white', border: '1px solid #e0e4e8',
            borderRadius: '8px', padding: '14px 16px',
          }}>
            {/* Program name + status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#2c3e50' }}>
                {p.program_name}
              </span>
              {p.status && (
                <span style={{
                  background: PROGRAM_STATUS_COLORS[p.status] || '#95a5a6', color: 'white',
                  borderRadius: '4px', padding: '1px 7px', fontSize: '11px', flexShrink: 0,
                }}>
                  {p.status}
                </span>
              )}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {p.next_actions.map(a => (
                <ActionButton
                  key={a.action_id}
                  action={a}
                  onClick={() => onActionClick(a)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ── Delivery card ─────────────────────────────────────────────────────────────

function DeliveryCard({ d, onDownload, onMarkDelivered, delivering }) {
  const id = d.delivery_id;
  const [showLeads, setShowLeads] = useState(false);
  const [accepting, setAccepting] = useState(false);

  // "Mark Delivered" now also accepts this delivery's leads (moves Pending
  // Review -> Under Review) in the same click — these were two separate
  // buttons doing overlapping "I'm done with this delivery" actions; a PM
  // shouldn't have to think about which one to click, or click both.
  const handleMarkDeliveredAndAccept = async () => {
    setAccepting(true);
    if (d.zb_results) {
      try {
        const res = await axios.post(`${API}/supplier-deliveries/${id}/accept`);
        const outcome = describeAcceptResult(res.data);
        if (outcome.warn) alert(outcome.text);
      } catch (err) {
        alert('Accepting leads failed: ' + (err.response?.data?.detail || err.message));
        // Held or cancelled by the supplier (409): Mark Delivered would be refused for the same
        // reason, so stop here instead of showing a second error.
        if (err.response?.status === 409) { setAccepting(false); return; }
      }
    }
    setAccepting(false);
    onMarkDelivered(id);
  };

  const handleDownloadBatchFeedback = () => {
    axios.get(`${API}/supplier-deliveries/${id}/export-batch-feedback`, { responseType: 'blob' })
      .then(res => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = `${(d.filename || `delivery_${id}`).replace(/\.[^.]+$/, '')}_batch_feedback.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Download failed'));
  };

  return (
    <div style={{ background: 'white', border: '1px solid #e0e4e8', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#2c3e50', marginBottom: '4px' }}>
            {d.program_name}
          </div>
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '6px' }}>
            {d.supplier_name && <span>{d.supplier_name}</span>}
            {d.supplier_name && d.uploaded_by_name && <span style={{ margin: '0 5px', color: '#ccc' }}>·</span>}
            {d.uploaded_by_name && <span>Uploaded by {d.uploaded_by_name}</span>}
          </div>
          <div style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#aaa' }}>📄</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.filename || '(unnamed file)'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
          <div style={{ fontSize: '11px', color: '#aaa' }}>{fmtDate(d.upload_date)}</div>
          {statusBadge(d.status)}
          <div style={{ display: 'flex', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
            {d.zb_results ? (
              <>
                <button onClick={handleDownloadBatchFeedback}
                  style={{ padding: '5px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  ⬇ Batch Feedback
                </button>
                <button onClick={() => onDownload(id, d.filename, true)}
                  style={{ padding: '5px 10px', background: 'white', border: '1px solid #3498db', color: '#3498db', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  ⬇ Original
                </button>
              </>
            ) : (
              <button onClick={() => onDownload(id, d.filename, true)}
                style={{ padding: '5px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                ⬇ Download
              </button>
            )}
            {onMarkDelivered && (
              <button onClick={handleMarkDeliveredAndAccept} disabled={delivering?.[id] || accepting}
                title="Moves this delivery's Pending Review leads to Under Review and marks the delivery Delivered to PM"
                style={{
                  padding: '5px 10px', border: 'none', borderRadius: '4px', fontSize: '12px',
                  background: (delivering?.[id] || accepting) ? '#ccc' : '#27ae60',
                  color: 'white', cursor: (delivering?.[id] || accepting) ? 'not-allowed' : 'pointer',
                }}>
                {accepting ? 'Accepting…' : delivering?.[id] ? 'Marking…' : '✓ Mark Delivered'}
              </button>
            )}
          </div>
          {d.zb_results && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '2px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowLeads(o => !o)}
                style={{ padding: '4px 10px', background: 'white', border: '1px solid #7f8c8d', color: '#555', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                {showLeads ? '▾' : '▸'} View Leads
              </button>
            </div>
          )}
        </div>
      </div>
      {(d.row_counts || d.zb_results) && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '12px 16px', background: '#fafbfc' }}>
          {d.row_counts && <RowBreakdown counts={d.row_counts} status={onMarkDelivered ? 'Pending' : 'Delivered to PM'} showReason />}
          {d.zb_results && <ZbSummary zbResults={d.zb_results} checkedAt={d.zb_checked_at} />}
        </div>
      )}
      {showLeads && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px 16px' }}>
          <DeliveryLeadsPanel id={id} />
        </div>
      )}
    </div>
  );
}

// Click-to-expand: lazy-fetches this program's Component→Goal status
// breakdown (same GET /goals/?program_id=&include_counts=true LeadQA's
// rollup uses) only on first expand, cached afterward so re-collapsing and
// re-opening doesn't refetch.
// ── Main page ─────────────────────────────────────────────────────────────────

export default function MyHomePM() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [delivering, setDelivering] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [allComponents, setAllComponents] = useState([]);
  const [breakdowns, setBreakdowns] = useState({}); // programId -> StatusBreakdownTable rows (goalsToPerGoalRows)
  const [leadStatusSummary, setLeadStatusSummary] = useState([]); // GET /programs/lead-summary rows — feeds LeadStatusSummaryBar

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/home/pm`);
      setData(res.data);
    } catch {
      setData({ programs: [], pending_deliveries: [], past_deliveries: [], tasks: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setStatuses(r.data)).catch(() => {});
    axios.get(`${API}/components/`).then(r => setAllComponents(r.data || [])).catch(() => {});
    axios.get(`${API}/programs/lead-summary`).then(r => setLeadStatusSummary(r.data || [])).catch(() => {});
  }, []);

  const loadBreakdown = (programId) => {
    axios.get(`${API}/goals/`, { params: { program_id: programId, include_counts: true } })
      .then(r => setBreakdowns(prev => ({ ...prev, [programId]: goalsToPerGoalRows(r.data || [], allComponents) })))
      .catch(() => setBreakdowns(prev => ({ ...prev, [programId]: [] })));
  };

  // See src/hooks/useTaskActions.js — was a hand-copied implementation
  // shared verbatim with SupplierPortal.js until 2026-09-01. `data.tasks`
  // lives nested inside this page's own combined payload state, so the
  // updater just re-derives a full `data` object around whatever the hook
  // hands back for `tasks`.
  const { removeTaskLocally, handleToggleCompleted, handleSnooze, handleDismiss } = useTaskActions(
    (updater) => setData(prev => (prev ? { ...prev, tasks: updater(prev.tasks) } : prev)),
    load,
  );
  // New tab, not in-app navigate() — a task button shouldn't take the PM
  // away from their own open task list to go do the thing.
  //
  // Routed by activity_type first, regardless of source (2026-08-28) — a
  // manually-assigned Week at a Glance "Scrub"/"Call reviews" item needs the
  // exact same destination as a system-generated one of the same type; only
  // activity types with no dedicated review page (Client delivery, Supplier
  // delivery, Spinup, Other) fall back to opening Week at a Glance itself
  // when manually assigned.
  // A system Scrub task always represents one specific pending
  // SupplierDelivery (task.delivery_id, parsed server-side from its
  // source_key — see home.py's _scrub_delivery_id()). Clicking through used
  // to just get the PM to the Pending Deliveries section, where they still
  // had to click Accept themselves before Lead QA Review would show
  // anything — a real reported round trip. Now it accepts the delivery
  // first (idempotent — a no-op if something else already accepted it) and
  // only then opens Lead QA Review, landing directly on reviewable leads.
  // window.open() is called synchronously, before the accept's await, and
  // redirected afterward — calling it only after an await risks browsers
  // treating the popup as no longer user-gesture-triggered and blocking it.
  const handleAcceptAndReview = async (task) => {
    const dest = task.program_id ? `/lead-qa?program_id=${task.program_id}` : '/lead-qa';
    const w = window.open('', '_blank');
    try {
      const res = await axios.post(`${API}/supplier-deliveries/${task.delivery_id}/accept`);
      removeTaskLocally(task.id);
      const outcome = describeAcceptResult(res.data);
      if (outcome.warn) window.alert(outcome.text);
    } catch (err) {
      // Already accepted, or some other hiccup — still worth navigating;
      // worst case the PM lands exactly where the old behavior would have.
    } finally {
      if (w) w.location.href = dest;
    }
  };

  const handleTaskCta = (task) => {
    if (task.activity_type === 'Scrub') {
      if (task.delivery_id) { handleAcceptAndReview(task); return; }
      // No specific delivery attached — either a PM manually added a
      // "Scrub" WAAG item, or (since the 2026-08-31 task-trigger-automation
      // round) a system-generated proactive placeholder for an expected
      // delivery that hasn't landed yet. Either way there's nothing to
      // auto-accept.
      //
      // Real reported bug (2026-09-01): "Start Lead QA" appeared to do
      // nothing. Root cause: the Pending Deliveries <Section> always
      // renders (its own content just switches to an empty-state message
      // when pending_deliveries.length === 0 — see the section below), so
      // `document.getElementById('pending-deliveries')` was ALWAYS truthy
      // and this branch always returned early after scrolling to what was
      // frequently an empty section — a scroll that's easy to not even
      // notice, reading as "the click did nothing." The comment this
      // replaced (dated 2026-08-28) assumed the section could be entirely
      // absent from the DOM; it never actually was. Checking the real data
      // instead of the DOM's mere presence is what makes "is there
      // something to scroll to" an actual signal.
      //
      // Scoped to this task's OWN program (2026-09-01, follow-up fix found
      // via code review) — the first version of this fix checked the PM's
      // entire pending_deliveries list, so an unrelated delivery sitting on
      // a completely different program would hijack the scroll destination
      // even though nothing relevant to Program A's task shows up there.
      if (data?.pending_deliveries?.some(d => d.program_id === task.program_id)) {
        document.getElementById('pending-deliveries')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // Nothing pending to scroll to (the common case for a proactive
      // placeholder) — a Scrub task must always end up at Lead QA Review,
      // never silently do nothing and never Week at a Glance.
      const dest = task.program_id ? `/lead-qa?program_id=${task.program_id}` : '/lead-qa';
      window.open(dest, '_blank');
      return;
    }
    if (task.activity_type === 'Client delivery') {
      // 2026-09-09: opens the Client Delivery Wizard now instead of Lead QA
      // Review — sending the accepted batch is the actual next action this
      // task represents, and the wizard does it without the download-CSV/
      // re-upload round trip through Client Leads Delivery. Same "always a
      // real destination, system-generated or manually-assigned, never Week
      // at a Glance" principle as the 2026-09-01 ask that first pointed this
      // at Lead QA Review.
      const dest = task.program_id ? `/client-delivery-wizard?program_id=${task.program_id}` : '/client-delivery-wizard';
      window.open(dest, '_blank');
      return;
    }
    if (task.activity_type === 'Call reviews') {
      // Real ask, 2026-09-01: "Call reviews (auto or proactive) should
      // always take you to Lead QA for that program" — the generic
      // manually-assigned fallback below (open Week at a Glance) isn't a
      // useful destination when a real one — Lead QA Review, to review the
      // pending calls — already exists and is exactly where the work
      // happens, regardless of whether this particular item is
      // system-generated or a manually-assigned WAAG entry.
      const dest = task.program_id ? `/lead-qa?program_id=${task.program_id}` : '/lead-qa';
      window.open(dest, '_blank');
      return;
    }
    if (task.source !== 'system') { window.open('/week-at-a-glance', '_blank'); return; }
    // Deep-link into the specific program instead of landing on an
    // unscoped Lead QA Review (see LeadQA.js's pendingProgramId).
    const dest = task.program_id ? `/lead-qa?program_id=${task.program_id}` : '/lead-qa';
    window.open(dest, '_blank');
  };

  const handleMarkDelivered = async (deliveryId) => {
    setDelivering(prev => ({ ...prev, [deliveryId]: true }));
    try {
      await axios.patch(`${API}/supplier-deliveries/${deliveryId}/deliver`);
      await load();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
      setDelivering(prev => ({ ...prev, [deliveryId]: false }));
    }
  };

  const downloadFile = (deliveryId, filename, original = false) => {
    const url = `${API}/supplier-deliveries/${deliveryId}/file${original ? '?original=true' : ''}`;
    axios.get(url, { responseType: 'blob' })
      .then(res => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = filename || `delivery_${deliveryId}`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Download failed'));
  };

  const handleActionClick = (action) => {
    const route = action.route || '';
    if (route.includes('#')) {
      // Same-page anchor — scroll to section
      const hash = route.split('#')[1];
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // New tab, matching the Tasks section's CTA buttons — don't take the
      // PM away from their own Home page to go do the thing.
      window.open(route, '_blank');
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>Loading…</div>;

  const { programs = [], pending_deliveries = [], past_deliveries = [], tasks = [] } = data || {};

  // Pending/Running (and "Closed – Not Invoiced") always visible; Closed – Invoiced
  // collapses to the bottom — same lookup_values.status_group classification used by
  // the Programs/Components/Goals list pages, applied here too instead of a 4th
  // independent implementation.
  const statusGroupMap = buildStatusGroupMap(statuses);
  const { active: activePrograms, closed: closedPrograms } = splitByStatusGroup(
    programs, { statusKey: 'status', groupMap: statusGroupMap, dateKey: 'flight_date_end' }
  );

  // programId -> status, so Past Deliveries can tell which of a PM's own programs
  // (already loaded above, every status included) each delivery belongs to.
  const programStatusById = Object.fromEntries(programs.map(p => [p.id, p.status]));

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '18px' }}>
        <h2 style={{ margin: '0 0 4px', color: '#2c3e50' }}>My Home</h2>
        <p style={{ margin: 0, color: '#7f8c8d', fontSize: '13px' }}>
          Your assigned programs and pending supplier deliveries awaiting review.
        </p>
      </div>

      {/* Cross-program status summary — one line, above Your Tasks */}
      <LeadStatusSummaryBar rows={leadStatusSummary} programIds={activePrograms.map(p => p.id)} />

      {/* Your Tasks — system-triggered + manually assigned Week at a Glance items */}
      <Section id="your-tasks" title="Your Tasks" count={tasks.length} countColor={tasks.length > 0 ? BADGE_ACTIVE : BADGE_NEUTRAL} defaultOpen={tasks.length > 0}>
        <TaskList
          tasks={tasks}
          onToggleCompleted={handleToggleCompleted}
          onSnooze={handleSnooze}
          onDismiss={handleDismiss}
          onCta={handleTaskCta}
        />
        <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => window.open('/week-at-a-glance', '_blank')}
            style={{ flexShrink: 0, padding: '7px 14px', background: 'white', border: '1px solid #ddd', color: '#2c3e50', borderRadius: '6px', cursor: 'pointer', fontSize: '12.5px', fontWeight: '600', whiteSpace: 'nowrap' }}>
            📅 Week at a Glance
          </button>
        </div>
      </Section>

      {/* Next Actions */}
      <NextActionsSection programs={programs} onActionClick={handleActionClick} />

      {/* Assigned Programs & Lead Status — shared ProgramCarousel with SupplierPortal.js's
          own "Assigned Programs & Lead Status" section (2026-08-31, condensed into a
          carousel per explicit ask + wireframe review — was a vertical stack of full
          ProgramStatusCards). No headerActions/renderStatusHeader passed — PM gets the
          plain breakdown, no download icons (those stay Supplier-only). */}
      <Section id="assigned-programs-status" title="Assigned Programs & Lead Status" count={programs.length} countColor={BADGE_NEUTRAL}>
        <ProgramCarousel
          emptyText="No programs assigned to you yet."
          items={activePrograms.map(p => ({
            id: p.id, program: p, rows: breakdowns[p.id], onExpand: loadBreakdown,
            includesCallReview: allComponents.some(c => String(c.program_id) === String(p.id) && c.includes_call_review),
          }))}
          closedItems={closedPrograms.map(p => ({
            id: p.id, program: p, rows: breakdowns[p.id], onExpand: loadBreakdown,
            includesCallReview: allComponents.some(c => String(c.program_id) === String(p.id) && c.includes_call_review),
          }))}
        />
      </Section>

      {/* Pending Deliveries */}
      <Section id="pending-deliveries" title="Pending Deliveries" count={pending_deliveries.length} countColor={pending_deliveries.length > 0 ? BADGE_ACTIVE : BADGE_NEUTRAL}>
        {pending_deliveries.length === 0 ? (
          <div style={{ padding: '24px', background: 'white', border: '1px solid #eee', borderRadius: '8px', color: '#aaa', textAlign: 'center', fontSize: '13px' }}>
            ✅ No pending deliveries — you're all caught up.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {pending_deliveries.map(d => (
              <DeliveryCard key={d.delivery_id} d={d}
                onDownload={downloadFile}
                onMarkDelivered={handleMarkDelivered}
                delivering={delivering} />
            ))}
          </div>
        )}
      </Section>

      {/* Past Deliveries */}
      <PastDeliveriesPanel deliveries={past_deliveries} downloadFile={downloadFile}
        programStatusById={programStatusById} statusGroupMap={statusGroupMap} />
    </div>
  );
}

function PastDeliveriesPanel({ deliveries, downloadFile, programStatusById, statusGroupMap }) {
  // Deliveries for a Closed program nest into their own collapsed section at the
  // bottom, same status_group classification used everywhere else on this page —
  // preserves the existing upload-date-desc order within each bucket (a plain
  // partition, not a re-sort; deliveries already arrive sorted correctly).
  const activeDeliveries = [];
  const closedDeliveries = [];
  deliveries.forEach(d => {
    const group = statusGroupMap[programStatusById[d.program_id]];
    (group === 'closed' ? closedDeliveries : activeDeliveries).push(d);
  });

  return (
    <Section id="past-deliveries" title="Past Deliveries" count={deliveries.length} countColor={BADGE_NEUTRAL}>
      {deliveries.length === 0 ? (
        <div style={{ padding: '20px', background: 'white', border: '1px solid #eee', borderRadius: '8px', color: '#aaa', textAlign: 'center', fontSize: '13px' }}>
          No past deliveries yet.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {activeDeliveries.map(d => (
              <DeliveryCard key={d.delivery_id} d={d} onDownload={downloadFile} />
            ))}
          </div>
          {closedDeliveries.length > 0 && (
            <CollapsibleSection label="Closed programs" count={closedDeliveries.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {closedDeliveries.map(d => (
                  <DeliveryCard key={d.delivery_id} d={d} onDownload={downloadFile} />
                ))}
              </div>
            </CollapsibleSection>
          )}
        </>
      )}
    </Section>
  );
}
